export enum LotTypeKey {
  ON_SALE = 'remate',
  ON_AUCTION = 'subasta',
}

export interface Auction {
  isRunning: boolean;
  runningOpenDate: any;
  runningCloseDate: any;
}

export type PanelVideoMode = 'preloaded' | 'live';

export class LotInfo {
  active: boolean;
  code: string;
  isPaused: boolean;
  cutting: boolean;
  editing: boolean;
  readonly videoMode: PanelVideoMode;

  constructor(
    active: boolean,
    code: string,
    isPaused: boolean,
    videoMode?: string,
    cutting: boolean = false,
    editing: boolean = false,
  ) {
    this.active = active;
    this.code = code;
    this.isPaused = isPaused;
    this.cutting = cutting;
    this.editing = editing;
    this.videoMode = LotInfo.normalizeVideoMode(videoMode);
  }

  private static normalizeVideoMode(raw: string | undefined): PanelVideoMode {
    if (typeof raw === 'string' && raw.length > 0 && raw === 'live') {
      return 'live';
    }
    return 'preloaded';
  }
}

export class LotData {
  code: string;
  bid: string;
  sysPrice: string;
  basePrice: string;
  type: string;
  weight: number;
  quantity: number;
  category: string;
  video: string;
  origin: string;
  providerName: string;
  observations: string;
  arrivedTime: string;

  constructor(
    code: string,
    bid: string,
    sysPrice: string,
    basePrice: string,
    type: string,
    weight: number,
    quantity: number,
    category: string,
    video: string = '',
    origin: string = '',
    providerName: string = '',
    observations: string = '',
    arrivedTime: string = '',
  ) {
    this.code = code;
    this.bid = bid;
    this.basePrice = basePrice;
    this.sysPrice = (+sysPrice + +bid).toString();
    this.type = type;
    this.weight = weight;
    this.quantity = quantity;
    this.category = category;
    this.video = video;
    this.origin = origin;
    this.providerName = providerName;
    this.observations = observations;
    this.arrivedTime = arrivedTime;
  }

  getAvgWeight() {
    return this.weight / this.quantity;
  }

  getAnimalPrice() {
    const kiloPrice = this.getKiloPrice();

    switch (this.type) {
      case LotTypeKey.ON_SALE:
        return +this.sysPrice;
      case LotTypeKey.ON_AUCTION:
        return this.getAvgWeight() * kiloPrice;
      default:
        return 0;
    }
  }

  getKiloPrice() {
    switch (this.type) {
      case LotTypeKey.ON_SALE:
        return +this.sysPrice / this.getAvgWeight();
      case LotTypeKey.ON_AUCTION:
        return +this.sysPrice;
      default:
        return 0;
    }
  }

  getNextBidValue() {
    return +this.sysPrice;
  }

  getTotalPrice() {
    switch (this.type) {
      case LotTypeKey.ON_SALE:
        return +this.sysPrice * this.quantity;
      case LotTypeKey.ON_AUCTION:
        return this.weight * this.getKiloPrice();
      default:
        return 0;
    }
  }

  static empty() {
    return new LotData('-', '', '', '', '', 0, 0, '', '', '', '', '', '');
  }
}

export class NextLotInfo {
  code: string;
  increment: string;
  base: string;
  type: string;
}

export enum BidOriginType {
  USER = 'USER',
  ASSISTED = 'ASSISTED',
  SCHEDULED = 'SCHEDULED',
}

export const BID_ORIGIN_LABELS: Record<BidOriginType, string> = {
  [BidOriginType.USER]: 'Usuario',
  [BidOriginType.ASSISTED]: 'Presencial',
  [BidOriginType.SCHEDULED]: 'Programada',
};

export const resolveBidOriginType = (source?: string, asistida?: boolean): BidOriginType => {
  if (source === 'AGENT') {
    return BidOriginType.SCHEDULED;
  }
  if (asistida === true) {
    return BidOriginType.ASSISTED;
  }
  return BidOriginType.USER;
};

export interface AssistedPendingWinner {
  readonly lotId: string;
  readonly racketName: string;
  readonly bidValue: number;
}

export const resolveAssistedPendingWinner = (
  highestBid: BidLot | null,
  lotId: string,
): AssistedPendingWinner | null => {
  if (!highestBid?.isAssistedPending) {
    return null;
  }

  return {
    lotId,
    racketName: highestBid.username,
    bidValue: highestBid.value,
  };
};

export class BidLot {
  readonly clientName: string;
  readonly date: Date;
  readonly isAssistedPending: boolean;
  readonly originType: BidOriginType;
  readonly username: string;
  readonly value: number;

  constructor(
    date: Date,
    username: string,
    value: number,
    clientName: string,
    originType: BidOriginType = BidOriginType.USER,
    isAssistedPending: boolean = false,
  ) {
    this.date = date;
    this.username = username;
    this.value = value;
    this.clientName = clientName;
    this.originType = originType;
    this.isAssistedPending = isAssistedPending;
  }
}
